import { Link } from 'react-router';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';

const LINK_SHAPE = PropTypes.shape({
  to: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  disabled: PropTypes.bool,
});

const ACCENT_STYLES = {
  primary: {
    section: 'border-primary-200 bg-primary-50/40',
    titleBorder: 'border-primary-400',
    cardHover: 'hover:bg-primary-200',
  },
  accent: {
    section: 'border-accent-200 bg-accent-50/50',
    titleBorder: 'border-accent-400',
    cardHover: 'hover:bg-accent-200',
  },
};

function resolveAccent(accent = 'primary') {
  return ACCENT_STYLES[accent] ?? ACCENT_STYLES.primary;
}

function LinkCard({ item, accent = 'primary' }) {
  const styles = resolveAccent(accent);
  const isDisabled = item.disabled === true || !item.to;

  const className = cn(
    'page-list__card min-h-30 rounded border border-gray-300 p-4',
    'bg-panel text-center text-lg font-medium text-gray-700 shadow-md',
    '@2xl:text-left',
    isDisabled
      ? 'cursor-not-allowed bg-gray-50 text-gray-400 opacity-80'
      : cn(
        'shortcut_transition',
        'hover:translate-y-[-4px] hover:shadow-lg',
        styles.cardHover,
      ),
  );

  const body = (
    <>
      <p>{item.title}</p>
      {item.subtitle ? (
        <p className={cn('mt-1 text-sm', isDisabled ? 'text-gray-400' : 'text-gray-500')}>
          {item.subtitle}
        </p>
      ) : null}
    </>
  );

  if (isDisabled) {
    return (
      <div
        key={item.to ?? item.title}
        className={className}
        aria-disabled="true"
      >
        {body}
      </div>
    );
  }

  return (
    <Link key={item.to} className={className} to={item.to}>
      {body}
    </Link>
  );
}

LinkCard.propTypes = {
  item: LINK_SHAPE.isRequired,
  accent: PropTypes.oneOf(['primary', 'accent']),
};

function LinkCardGrid({ links, accent = 'primary' }) {
  return (
    <div
      className="
        mt-5 grid grid-cols-2 gap-2.5
        @2xl:grid-cols-3
        @3xl:grid-cols-4
      "
    >
      {links.map((item) => (
        <LinkCard
          key={item.to ?? item.title}
          item={item}
          accent={accent}
        />
      ))}
    </div>
  );
}

LinkCardGrid.propTypes = {
  links: PropTypes.arrayOf(LINK_SHAPE).isRequired,
  accent: PropTypes.oneOf(['primary', 'accent']),
};

function PageListSection({ section }) {
  const styles = resolveAccent(section.accent);

  return (
    <section
      className={cn(
        'rounded-lg border p-4 shadow-sm',
        styles.section,
      )}
    >
      <h2 className="page_title mb-0!">
        <span className={cn('border-l-5 pl-2.5', styles.titleBorder)}>
          {section.title}
        </span>
      </h2>
      {section.description ? (
        <p className="mt-2 text-sm text-gray-600">{section.description}</p>
      ) : null}
      <LinkCardGrid links={section.links} accent={section.accent} />
    </section>
  );
}

PageListSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    accent: PropTypes.oneOf(['primary', 'accent']),
    description: PropTypes.string,
    links: PropTypes.arrayOf(LINK_SHAPE).isRequired,
  }).isRequired,
};

/**
 * Hub／主控台卡片網格。
 *
 * - 舊用法：`title` + `linkList`（單一區塊；hover 用 primary）
 * - 新用法：`sections`（多區＋accent；可選頁級 `title`）
 * - `linkList`／`links` 項可設 `disabled: true`（占位卡，不導頁）
 */
const PageList = ({ title, linkList, sections }) => {
  const hasSections = Array.isArray(sections) && sections.length > 0;

  if (hasSections) {
    return (
      <div className="@container">
        {title ? (
          <h1 className="page_title mb-4!">
            <span className="border-l-5 border-primary-400 pl-2.5">{title}</span>
          </h1>
        ) : null}
        <div className="space-y-6">
          {sections.map((section) => (
            <PageListSection
              key={section.id ?? section.title}
              section={section}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="@container">
      <div>
        <h2 className="page_title">
          <span className="border-l-5 border-primary-400 pl-2.5">{title}</span>
        </h2>
        <LinkCardGrid links={linkList ?? []} accent="primary" />
      </div>
    </div>
  );
};

PageList.propTypes = {
  title: PropTypes.string,
  linkList: PropTypes.arrayOf(LINK_SHAPE),
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string.isRequired,
      accent: PropTypes.oneOf(['primary', 'accent']),
      description: PropTypes.string,
      links: PropTypes.arrayOf(LINK_SHAPE).isRequired,
    }),
  ),
};

export default PageList;
